/**
 * The "Columns" menu (P30) — which columns this table shows.
 *
 * It lists EVERY column, hidden ones included, which is the whole point: a
 * column that disappears from the table and from the menu that hides it is a
 * column nobody can bring back. Ticking is per table and per browser profile
 * (`useHiddenColumns`), never per selection.
 *
 * Its own click handling stops at the button, like `ColumnFilter`'s — it sits
 * above the table rather than in a header cell, but the two menus behave the
 * same way on purpose.
 *
 * ## It carries the app's one What's-New anchor (P41)
 *
 * This button did not exist before the design update, and the four people in the pilot used the
 * boards for weeks without it — so a marker here points at something that is genuinely new *to
 * them*, which is the only thing that earns one. `pure-app` refused markers on six entries after
 * measuring that everybody who could have noticed had already used the elements; the opposite is
 * true here.
 *
 * `reveal` opens the menu, so "Show me" in the panel actually shows the reader the thing rather
 * than scrolling to a closed button, and the hook's `ref` moves the keyboard there with it.
 * `markUsed` hangs on the CLICK and not on a `useEffect`: a marker cleared the moment the button
 * rendered would be cleared for everybody who never looked at it.
 *
 * Mounted once at a time although both boards render this component — they are separate routes,
 * so two registrations of the same id cannot overlap.
 */
import { useEffect, useRef, useState } from 'react';
import { Columns } from 'lucide-react';
import { FOCUS_RING } from './focus';
import { trackFeature } from '../../services/usage';
import { useWhatsNewAnchor } from '../../whats-new/useWhatsNewAnchor';
import { NewMarker } from '../../whats-new/NewMarker';

export interface ChooserColumn {
    key: string;
    label: string;
    title?: string;
}

export function ColumnChooser({
    columns, hidden, onToggle, onShowAll,
}: {
    columns: ChooserColumn[];
    hidden: string[];
    onToggle: (key: string) => void;
    onShowAll: () => void;
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    // Outside a `WhatsNewProvider` this answers "nothing is new" instead of throwing, which is
    // why the kit's own tests need no provider (`WhatsNewProvider.tsx`, `INERT`).
    const { isNew, markUsed, spotlight, ref: anchorRef } = useWhatsNewAnchor<HTMLButtonElement>(
        'control:columns',
        { reveal: () => setOpen(true) },
    );

    useEffect(() => {
        if (!open) return;
        const onDocMouseDown = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDocMouseDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocMouseDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    return (
        <span className="relative inline-block" ref={ref}>
            <button
                type="button"
                ref={anchorRef}
                onClick={() => { markUsed(); setOpen(o => !o); }}
                aria-expanded={open}
                // P40: the ring was written out here, character for character, instead
                // of imported. `ui/focus.ts` exists so there is ONE of it — a hand copy
                // is a second implementation that stops matching the day the constant
                // changes, and nothing would notice. Found by `scan-focus`.
                className={`inline-flex h-8 items-center gap-1 rounded-ctl border border-hair bg-surface px-2.5 text-xs font-medium text-body hover:bg-hover ${FOCUS_RING}${spotlight ? ' ring-2 ring-focus ring-offset-2' : ''}`}
            >
                <Columns className="h-3 w-3" />
                Columns{hidden.length > 0 ? ` (${hidden.length} hidden)` : ''}
                {isNew && <NewMarker />}
            </button>
            {open && (
                <div
                    role="dialog"
                    aria-label="Columns"
                    className="absolute right-0 top-full z-30 mt-1 w-60 rounded-card border border-hair bg-surface p-2 shadow-overlay"
                >
                    <div className="mb-1 flex items-center justify-between px-0.5 text-xs text-info">
                        <span className="text-muted">Shown in this table</span>
                        <button type="button" onClick={() => { trackFeature('columns-show-all'); onShowAll(); }}>Show all</button>
                    </div>
                    <div className="max-h-72 overflow-auto border-t pt-1">
                        {columns.map(c => (
                            <label
                                key={c.key}
                                title={c.title}
                                className="flex cursor-pointer items-center gap-1.5 rounded px-1 py-0.5 text-xs hover:bg-gray-50"
                            >
                                <input
                                    type="checkbox"
                                    checked={!hidden.includes(c.key)}
                                    onChange={() => {
                                        // Usage telemetry: `shown` is the state the column is moving TO.
                                        trackFeature('columns-toggle', { shown: hidden.includes(c.key) });
                                        onToggle(c.key);
                                    }}
                                />
                                <span className="truncate">{c.label}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </span>
    );
}
